import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Input, TextArea } from '@heroui/react'
import { api } from '../lib/api'
import {
  ALL_PLACEHOLDERS,
  MAX_AHEAD,
  MAX_WORDS,
  PLACEHOLDERS,
  PREFIX_SHAPE,
  fillPieces,
  filledText,
  refusedPlaceholders,
  sampleLink,
  sampleValues,
  segments,
  wordsIn,
  type Piece,
  type Placeholder,
} from '../lib/smsText'
import type { StaffKey } from '../lib/staffText'
import type { NotificationSettings, NotificationsView, TextTrigger, VisitField } from '../lib/types'
import { Icon } from '../ui/icons'
import { StatusChip } from '../ui/parts'
import { useStaffText } from '../ui/StaffText'
import { GatewayDrawer, NotificationsOwner } from './NotificationsOwner'
import { useTextLanguages } from '../lib/i18n/useCustomerLanguages'
import { languageNames, missingLanguages } from '../lib/i18n/missing'
import { resolveText } from '../lib/i18n/customer'
import { MissingMark } from '../ui/MissingMark'
import { SameSwitch } from '../ui/SameSwitch'
import { SAME, isSame, withWords } from '../lib/i18n/same'
import { Toggle, useFlash, useSave } from './sectionKit'
import { IconTile, InnerPage, ListCard, ListRow, ListTable, NameCell, Notice, PageHead, RowArrow, Seg, SettingRow, SettingsCard, SwitchRow, useOpenRecord } from './settingsKit'
import '../styles/org.css'
import '../styles/connect.css'
import { Pick as SetupPick } from './setupKit'

/**
 * Texts to customers: whether they are sent, from which field, at which moments,
 * in which words, to which numbers. The organisation's administrator's. The
 * gateway they go through is the installation owner's, shown here as a fact and
 * edited in NotificationsOwner, which only tenant.manage sees — the directory's
 * split (admin/Directory.tsx).
 *
 * The screen is built around the phone, because the question an administrator
 * has is "what will they actually read, and what does it cost us", and a template
 * full of {{braces}} answers neither. The rows and the phone show the server's own
 * samples of the saved words; the drawer works the same arithmetic out in the
 * browser while the words are typed (lib/smsText), because an Azerbaijani text is
 * billed in 70-character pieces and a template that looks short is often three.
 */
const TRIGGERS: TextTrigger[] = ['issued', 'ahead', 'called']

// Spelled out rather than built from the trigger: say() narrows only a literal.
const MOMENT: Record<TextTrigger, StaffKey> = {
  issued: 'org.sms.moment.issued',
  ahead: 'org.sms.moment.ahead',
  called: 'org.sms.moment.called',
}
const PLACEHOLDER_NAME: Record<Placeholder, StaffKey> = {
  number: 'org.sms.ph.number',
  branch: 'org.sms.ph.branch',
  service: 'org.sms.ph.service',
  ahead: 'org.sms.ph.ahead',
  wait: 'org.sms.ph.wait',
  desk: 'org.sms.ph.desk',
  follow_url: 'org.sms.ph.follow_url',
}

/** Whether a moment sends at all: the ahead text has no switch of its own on the
 *  server, and 0 people ahead is how it is switched off. */
const sends = (s: NotificationSettings, t: TextTrigger) => (t === 'ahead' ? s.aheadAt > 0 : s[t])

/** The steps of the Who and Gateway pages. */
const WHO = 'who'
const GATEWAY = 'gateway'

const fieldName = (f: VisitField, lang: string) => resolveText(f.name, lang) || f.key

export function Notifications({ organisation, canConnect }: { organisation?: string; canConnect: boolean }) {
  const { lang, say } = useStaffText()
  const [view, setView] = useState<NotificationsView | null>(null)
  const [phoneFields, setPhoneFields] = useState<VisitField[]>([])
  const [loadError, setLoadError] = useState('')
  const TEXT_LANGUAGES = useTextLanguages(organisation)
  const [picked, setShown] = useState<string>(lang)
  const shown = TEXT_LANGUAGES.some(([c]) => c === picked) ? picked : TEXT_LANGUAGES[0]![0]
  const { flash, toast } = useFlash()

  const load = useCallback(async () => {
    const [v, f] = await Promise.all([api.notifications(organisation), api.fields(organisation)])
    setView(v)
    setPhoneFields(f.fields.filter((x) => x.type === 'phone' && x.active))
  }, [organisation])

  useEffect(() => {
    load().catch(() => setLoadError(say('notify.notLoaded')))
  }, [load, say])

  // The master switch saves at once, so its refusal is said on the screen rather
  // than in a drawer that is not open.
  const master = useSave(load, flash)

  // A text, who receives texts, and the gateway each open on a page of their own:
  // #notifications/<moment>, #notifications/who, #notifications/gateway.
  const rec = useOpenRecord(TRIGGERS, (t) => t, Boolean(view), [WHO, ...(canConnect ? [GATEWAY] : [])])
  const editing = rec.row
  const back = say('admin.nav.notifications')

  const head = <PageHead title={back} line={say('connect.line.notifications')} />
  if (view && editing) {
    return (
      <>
        <MomentDrawer
          key={editing}
          trigger={editing}
          view={view}
          values={(lang) => sampleValues(sampleLink(view), lang)}
          organisation={organisation}
          onClose={rec.close}
          onSaved={load}
          flash={flash}
        />
        {toast}
      </>
    )
  }
  if (view && rec.code === WHO) {
    return (
      <>
        <WhoDrawer view={view} phoneFields={phoneFields} organisation={organisation} onClose={rec.close} onSaved={load} flash={flash} />
        {toast}
      </>
    )
  }
  if (view && canConnect && rec.code === GATEWAY) {
    return (
      <>
        <GatewayDrawer back={back} organisation={organisation} view={view} onSaved={load} onClose={rec.close} flash={flash} />
        {toast}
      </>
    )
  }
  if (!view) {
    return (
      <>
        {head}
        {loadError && <Notice>{loadError}</Notice>}
      </>
    )
  }

  const s = view.settings
  const field = phoneFields.find((f) => f.key === s.phoneField)
  const on = TRIGGERS.filter((t) => sends(s, t))
  const link = sampleLink(view)
  const values = (lang: string) => sampleValues(link, lang)
  // Switching on needs a phone field; switching off never waits for anything.
  const masterLocked = !s.enabled && !field

  return (
    <>
      {head}
      {master.error && <Notice className="connect-gap">{master.error}</Notice>}
      <div className="org-sms">
        <div className="org-sms__main">
          <SettingsCard label={say('org.sms.master')}>
            <SwitchRow
              label={say('org.sms.master')}
              status={s.enabled ? say('org.sms.onCount', { n: on.length }) : say('org.sms.off')}
              checked={s.enabled}
              disabled={master.busy || masterLocked}
              dimmed={masterLocked}
              onChange={() =>
                void master.save(
                  () => api.saveNotifications({ ...s, enabled: !s.enabled }, organisation),
                  say(s.enabled ? 'org.sms.switchedOff' : 'org.sms.switchedOn'),
                )
              }
            />
            {(!field || !view.gatewayConfigured) && (
              <div className="connect-cardnote connect-cardnote--end">
                {!field && (
                  <p className="connect-callout">
                    <Icon name="alert" size={15} /> {say(phoneFields.length ? 'org.sms.needsField' : 'notify.noPhoneField')}
                  </p>
                )}
                {!view.gatewayConfigured && (
                  <p className="connect-callout">
                    <Icon name="alert" size={15} /> {say('org.sms.noGatewayWarn')}
                  </p>
                )}
              </div>
            )}
          </SettingsCard>

          <ListCard title={say('org.sms.texts')} count={say('connect.sms.onOf', { n: s.enabled ? on.length : 0, of: TRIGGERS.length })}>
            <ListTable
              label={say('org.sms.texts')}
              cols="minmax(0, 1fr) 96px 104px 16px"
              head={[say('connect.sms.col.text'), say('connect.sms.col.length'), say('connect.col.status'), '']}
            >
              {TRIGGERS.map((t) => {
                const sample = view.samples.find((x) => x.trigger === t && x.language === shown)
                const length = sample ? segments(sample.text).length : 0
                const off = !sends(s, t) || !s.enabled
                return (
                  <ListRow key={t} off={off} label={say(MOMENT[t])} onOpen={() => rec.open(t)}>
                    <NameCell
                      tile={<IconTile icon="message" tone={off ? 6 : 1} size="sm" />}
                      name={
                        <>
                          {say(MOMENT[t])}{' '}
                          <MissingMark codes={missingLanguages(s.templates[t], TEXT_LANGUAGES.map(([c]) => c))} />
                        </>
                      }
                      sub={t === 'ahead' && s.aheadAt > 0 ? say('org.sms.aheadWhen', { n: s.aheadAt }) : undefined}
                    />
                    <span className="org-moment__cost" data-high={(sample && sample.segments > 2) || undefined}>
                      {sample && (
                        <>
                          <strong>{say('notify.segments', { n: sample.segments })}</strong>
                          {say('org.sms.chars', { n: length })}
                        </>
                      )}
                    </span>
                    <span>
                      <StatusChip tone={off ? undefined : 'ok'}>{say(off ? 'org.sms.notSent' : 'connect.on')}</StatusChip>
                    </span>
                    <RowArrow />
                  </ListRow>
                )
              })}
            </ListTable>
          </ListCard>

          <SettingsCard title={say('org.sms.who')}>
            <SettingRow
              control
              label={field ? say('org.sms.numbersFrom', { field: fieldName(field, lang) }) : say('org.sms.noField')}
              status={s.allowedPrefixes.length ? say('org.sms.only', { list: s.allowedPrefixes.join(', ') }) : say('org.sms.noPrefixes')}
            >
              <Button variant="secondary" size="sm" onPress={() => rec.nav.open(WHO)}>
                <Icon name="edit" size={14} /> {say('connect.edit')}
              </Button>
            </SettingRow>
          </SettingsCard>

          <SettingsCard title={say('org.sms.gateway')}>
            {canConnect ? (
              <NotificationsOwner view={view} onEdit={() => rec.nav.open(GATEWAY)} />
            ) : (
              // The server names the host; nothing here parses an address, which
              // could throw on one the owner saved in a shape URL() does not take.
              <SettingRow
                label={view.gatewayConfigured ? say('org.sms.sentThrough', { host: view.gatewayHost }) : say('org.sms.noGatewayRow')}
                description={say('org.sms.ownerOnly')}
              />
            )}
          </SettingsCard>
        </div>

      <aside className="org-sms__side" aria-label={say('org.sms.phoneAria')}>
        {TEXT_LANGUAGES.length > 1 && (
          <Seg
            className="org-langs"
            label={say('org.sms.previewLang')}
            value={shown}
            onChange={setShown}
            options={TEXT_LANGUAGES.map(([code, name]) => ({ value: code, label: name }))}
          />
        )}
        <div className="org-phone" lang={shown}>
          <div className="org-phone__head">
            <span className="org-phone__avatar" aria-hidden="true">
              <Icon name="phone" size={18} />
            </span>
          </div>
          <div className="org-phone__screen">
            {!s.enabled || on.length === 0 ? (
              <p className="org-phone__empty">{say('org.sms.nothingSent')}</p>
            ) : (
              on.map((t) => {
                const sample = view.samples.find((x) => x.trigger === t && x.language === shown)
                const pieces = fillPieces(wordsIn(s.templates[t], shown), values(shown))
                // The server's sample is the truth; the marked pieces are only how
                // it is drawn, and are used only when they say the same thing.
                const drawn: Piece[] =
                  sample && filledText(pieces) !== sample.text ? [{ text: sample.text, kind: 'words' }] : pieces
                return (
                  <button
                    type="button"
                    key={t}
                    className="org-bubble"
                    onClick={() => rec.open(t)}
                    aria-label={say('org.sms.editText', { name: say(MOMENT[t]) })}
                  >
                    <Filled pieces={drawn} />
                  </button>
                )
              })
            )}
          </div>
        </div>
      </aside>
      </div>

      {toast}
    </>
  )
}

/** The words with each filled-in value marked, so it is clear what changes per visit. */
function Filled({ pieces }: { pieces: Piece[] }) {
  return (
    <span className="org-bubble__text">
      {pieces.map((p, i) =>
        p.kind === 'words' ? (
          <span key={i}>{p.text}</span>
        ) : (
          <span
            key={i}
            className={p.kind === 'unknown' ? 'org-fill org-fill--bad' : 'org-fill'}
            data-link={p.kind === 'link' || undefined}
          >
            {p.text}
          </span>
        ),
      )}
    </span>
  )
}

function MomentDrawer({
  trigger,
  view,
  values,
  organisation,
  onClose,
  onSaved,
  flash,
}: {
  trigger: TextTrigger
  view: NotificationsView
  values: (lang: string) => Record<string, string>
  organisation?: string
  onClose: () => void
  onSaved: () => Promise<void>
  flash: (text: string) => void
}) {
  const { say } = useStaffText()
  const saved = view.settings
  const [on, setOn] = useState(sends(saved, trigger))
  const [aheadAt, setAheadAt] = useState(saved.aheadAt > 0 ? saved.aheadAt : 3)
  const [words, setWords] = useState<Record<string, string>>(() => ({ ...(saved.templates[trigger] ?? {}) }))
  const TEXT_LANGUAGES = useTextLanguages(organisation)
  const def = TEXT_LANGUAGES[0]![0]
  const same = isSame(words)
  // One box when the text is the same in every language, keyed SAME.
  const boxesShown: Array<[string, string]> = same ? [[SAME, say('org.sms.text')]] : TEXT_LANGUAGES
  const [picked, setLast] = useState<string>(() => (isSame(saved.templates[trigger]) ? SAME : TEXT_LANGUAGES[0]![0]))
  const last = boxesShown.some(([c]) => c === picked) ? picked : boxesShown[0]![0]
  const boxes = useRef<Record<string, HTMLTextAreaElement | null>>({})
  const edit = (code: string, value: string) => setWords(code === SAME ? withWords(words, def, value, def) : { ...words, [code]: value })
  const { busy, error, save } = useSave(
    useCallback(async () => {
      await onSaved()
      onClose()
    }, [onSaved, onClose]),
    flash,
  )

  const problems = useMemo(() => {
    const out: string[] = []
    for (const code of same ? [SAME] : TEXT_LANGUAGES.map(([c]) => c)) {
      const w = words[code] ?? ''
      for (const key of refusedPlaceholders(trigger, w)) {
        const token = `{{${key}}}`
        if (key === 'desk') out.push(say('org.sms.deskOnlyCalled', { token }))
        else if (key === 'follow_url') out.push(say('org.sms.linkOnlyFirst', { token }))
        else if ((ALL_PLACEHOLDERS as readonly string[]).includes(key)) out.push(say('org.sms.notKnownYet', { token }))
        else out.push(say('org.sms.unknownToken', { token }))
      }
      if ([...w].length > MAX_WORDS) out.push(say('org.sms.tooLong', { n: [...w].length, max: MAX_WORDS }))
    }
    return [...new Set(out)]
  }, [words, trigger, say, same, TEXT_LANGUAGES])
  const aheadBad = trigger === 'ahead' && on && (!Number.isInteger(aheadAt) || aheadAt < 1 || aheadAt > MAX_AHEAD)
  const valid = problems.length === 0 && !aheadBad

  const insert = (key: string) => {
    const el = boxes.current[last]
    const cur = words[last] ?? ''
    const at = el ? (el.selectionStart ?? cur.length) : cur.length
    const token = `{{${key}}}`
    edit(last, cur.slice(0, at) + token + cur.slice(at))
    requestAnimationFrame(() => {
      el?.focus()
      el?.setSelectionRange(at + token.length, at + token.length)
    })
  }

  const submit = () => {
    const next: NotificationSettings = {
      ...saved,
      templates: { ...saved.templates, [trigger]: words },
      ...(trigger === 'issued' ? { issued: on } : {}),
      ...(trigger === 'called' ? { called: on } : {}),
      ...(trigger === 'ahead' ? { aheadAt: on ? aheadAt : 0 } : {}),
    }
    // Saved either way; a language the words lack is sent another language's.
    const miss = missingLanguages(words, TEXT_LANGUAGES.map(([c]) => c))
    void save(
      () => api.saveNotifications(next, organisation),
      miss.length > 0 ? say('content.savedMissing', { langs: languageNames(miss) }) : say('org.sms.saved'),
    )
  }

  return (
    <InnerPage
      back={say('admin.nav.notifications')}
      title={say(MOMENT[trigger])}
      onClose={onClose}
      onSubmit={submit}
      valid={valid}
      busy={busy}
      submitLabel={say('org.sms.saveText')}
    >
      {error && <Notice>{error}</Notice>}
      <Toggle
        title={say('org.sms.send')}
        hint={trigger === 'ahead' ? undefined : say(trigger === 'issued' ? 'org.sms.whenIssued' : 'org.sms.whenCalled')}
        checked={on}
        onChange={setOn}
      />

      {trigger === 'ahead' && on && (
        <div className="grid2 grid2--rev">
          <label className="field field--narrow">
            <span>{say('org.sms.peopleAhead')}</span>
            <Input
              type="number"
              min={1}
              max={MAX_AHEAD}
              value={aheadAt || ''}
              aria-invalid={aheadBad}
              onChange={(e) => setAheadAt(Number(e.target.value))}
            />
          </label>
        </div>
      )}

      {boxesShown.map(([code, name]) => {
        const text = wordsIn(words, code === SAME ? def : code)
        const pieces = fillPieces(text, values(code === SAME ? def : code))
        const cost = segments(filledText(pieces))
        const own = (words[code] ?? '').trim() !== ''
        return (
          <div key={code} className="field org-words">
            <label className="org-words__label" htmlFor={`org-w-${code}`}>
              {boxesShown.length > 1 ? name : say('org.sms.text')}
              {!own && text && <span className="org-none">{say('org.sms.borrowed')}</span>}
            </label>
            <TextArea
              fullWidth
              id={`org-w-${code}`}
              ref={(el) => {
                boxes.current[code] = el
              }}
              className="org-textarea"
              rows={3}
              lang={code === SAME ? undefined : code}
              value={words[code] ?? ''}
              aria-invalid={refusedPlaceholders(trigger, words[code] ?? '').length > 0 || undefined}
              onFocus={() => setLast(code)}
              onChange={(e) => edit(code, e.target.value)}
            />
            <div className="org-words__meta">
              {text ? (
                <span className="org-bubble org-bubble--static">
                  <Filled pieces={pieces} />
                </span>
              ) : (
                <span className="org-none">{say('org.sms.shipped')}</span>
              )}
              {text && (
                <span className="org-words__cost" data-high={cost.parts > 2 || undefined}>
                  {say('org.sms.chars', { n: cost.length })}, <strong>{say('notify.segments', { n: cost.parts })}</strong>
                </span>
              )}
            </div>
          </div>
        )
      })}
      {(TEXT_LANGUAGES.length > 1 || same) && <SameSwitch value={words} onChange={setWords} defaultLang={def} />}
      {problems.map((p) => (
        <p key={p} className="field-error org-tight">
          {p}
        </p>
      ))}

      <fieldset className="field">
        <legend title={say('org.sms.ucs2')}>
          {boxesShown.length > 1 ? say('org.sms.insertInto', { lang: boxesShown.find(([c]) => c === last)?.[1] ?? '' }) : say('org.sms.insert')}
        </legend>
        <div className="picklist__add org-chips">
          {ALL_PLACEHOLDERS.filter((p) => PLACEHOLDERS[trigger].includes(p)).map((p) => (
            <Button key={p} variant="tertiary" size="sm" onPress={() => insert(p)}>
              <Icon name="plus" size={14} /> {say(PLACEHOLDER_NAME[p])}
            </Button>
          ))}
        </div>
      </fieldset>
    </InnerPage>
  )
}

function WhoDrawer({
  view,
  phoneFields,
  organisation,
  onClose,
  onSaved,
  flash,
}: {
  view: NotificationsView
  phoneFields: VisitField[]
  organisation?: string
  onClose: () => void
  onSaved: () => Promise<void>
  flash: (text: string) => void
}) {
  const { lang, say } = useStaffText()
  const saved = view.settings
  const [phoneField, setPhoneField] = useState(saved.phoneField)
  const [prefixes, setPrefixes] = useState(saved.allowedPrefixes.join(', '))
  const { busy, error, save } = useSave(
    useCallback(async () => {
      await onSaved()
      onClose()
    }, [onSaved, onClose]),
    flash,
  )
  const list = prefixes
    .split(',')
    .map((p) => p.trim().replace(/ /g, ''))
    .filter(Boolean)
  const bad = list.filter((p) => !PREFIX_SHAPE.test(p))
  // Texts that are on need somewhere to go; texts that are off may be set up
  // piece by piece (notify.Settings.Validate asks the same).
  const empty = saved.enabled && list.length === 0
  const valid = bad.length === 0 && !empty

  return (
    <InnerPage
      back={say('admin.nav.notifications')}
      title={say('org.sms.who')}
      onClose={onClose}
      onSubmit={() =>
        void save(
          () => api.saveNotifications({ ...saved, phoneField, allowedPrefixes: list }, organisation),
          say('org.sms.whoSaved'),
        )
      }
      valid={valid}
      busy={busy}
      submitLabel={say('common.save')}
    >
      {error && <Notice>{error}</Notice>}
      <div className="field">
        <span id="org-phone-field">{say('org.sms.phoneField')}</span>
        <SetupPick
          labelledBy="org-phone-field"
          value={phoneField}
          onChange={setPhoneField}
          placeholder={say('org.sms.chooseField')}
          options={[
            { value: '', label: say('org.sms.noFieldOption') },
            ...phoneFields.map((f) => ({ value: f.key, label: fieldName(f, lang), note: f.key })),
          ]}
        />
        {phoneFields.length === 0 && <small>{say('notify.noPhoneField')}</small>}
      </div>
      <label className="field">
        <span title={say('org.sms.prefixesHelp')}>{say('org.sms.prefixes')}</span>
        <Input
          fullWidth
          value={prefixes}
          aria-invalid={bad.length > 0 || empty}
          onChange={(e) => setPrefixes(e.target.value)}
          placeholder="+994"
        />
      </label>
      {bad.length > 0 && <p className="field-error">{say('org.sms.badPrefix', { list: bad.join(', ') })}</p>}
      {empty && <p className="field-error">{say('org.sms.needPrefix')}</p>}
    </InnerPage>
  )
}
